/**
 * Activity report — who did what, per dealer, for any day or range.
 *
 *  - Range presets (today, yesterday, 2 days ago, this week, …) or a custom
 *    pair of dates, all in the admin's local time.
 *  - Dealer filter: everyone by default, or any selection.
 *  - One row per dealer (or per dealer per day with "Group by day"): sessions,
 *    product views, DISTINCT products (listed), categories, searches, wishlist
 *    saves, first/last activity. Click a row for that dealer's full timeline.
 *  - Two CSV exports: the summary table as shown, or every event in the range
 *    for the selected dealers.
 *
 * Data: activity_logs between the range bounds, fetched once per range (see
 * useActivityRange) and aggregated in the browser (lib/activityReport).
 */
import { useMemo, useState } from 'react';
import type { User } from '@mitang/shared';
import { useUsers } from '../hooks/useFirestore';
import { ACTIVITY_RANGE_MAX, useActivityRange } from '../hooks/useActivityRange';
import {
  EVENT_CSV_COLUMNS,
  RANGE_PRESETS,
  SUMMARY_CSV_COLUMNS,
  aggregateByDay,
  aggregateByDealer,
  customRange,
  describeRange,
  eventCsvRow,
  inputDate,
  presetRange,
  rangeSlug,
  reportTotals,
  summaryCsvRow,
  type DealerActivityRow,
  type RangePreset,
} from '../lib/activityReport';
import { toCsv, downloadCsv } from '../lib/csv';
import { formatDateTime, timeAgo } from '../lib/format';
import { dayKey, dayLabel } from '../lib/time';
import { Badge, Button, Card, SectionHeader, StatusPill } from '../components/ui';
import { StatCard } from '../components/StatCard';
import { EmptyState, ErrorState, LoadingState } from '../components/States';
import { ActivityTimeline } from '../components/ActivityTimeline';

type SortKey = 'dealer' | 'day' | 'sessions' | 'productViews' | 'distinctProducts' | 'distinctCategories' | 'searches' | 'wishlisted' | 'events' | 'lastTs';
type SortDir = 'asc' | 'desc';

const inputCls =
  'rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500';

function matchesDealer(u: User, needle: string): boolean {
  if (!needle) return true;
  const n = needle.toLowerCase();
  return [u.name, u.companyName, u.city, u.email, u.mobile].some((f) => (f ?? '').toLowerCase().includes(n));
}

function compareRows(a: DealerActivityRow, b: DealerActivityRow, key: SortKey, dir: SortDir): number {
  const m = dir === 'asc' ? 1 : -1;
  switch (key) {
    case 'dealer':
      return m * a.user.name.localeCompare(b.user.name);
    case 'day':
      return m * (a.day ?? '').localeCompare(b.day ?? '');
    case 'lastTs':
      return m * ((a.lastTs ?? 0) - (b.lastTs ?? 0));
    default:
      return m * (a[key] - b[key]);
  }
}

export function ActivityReportPage(): JSX.Element {
  const users = useUsers();

  // ---- range ---------------------------------------------------------------
  const [preset, setPreset] = useState<RangePreset>('today');
  const [customFrom, setCustomFrom] = useState(inputDate(Date.now()));
  const [customTo, setCustomTo] = useState('');
  const range = useMemo(
    () => (preset === 'custom' ? customRange(customFrom, customTo) : presetRange(preset)),
    [preset, customFrom, customTo],
  );
  const logs = useActivityRange(range?.from ?? null, range?.to ?? null);
  const multiDay = range ? dayKey(range.from) !== dayKey(range.to) : false;

  // ---- dealers -------------------------------------------------------------
  const [selected, setSelected] = useState<Set<string>>(new Set()); // empty = everyone
  const [dealerSearch, setDealerSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const shownDealers = useMemo(
    () => users.data.filter((u) => matchesDealer(u, dealerSearch)).sort((a, b) => a.name.localeCompare(b.name)),
    [users.data, dealerSearch],
  );
  const selectedUsers = useMemo(
    () => (selected.size ? users.data.filter((u) => selected.has(u.id)) : users.data),
    [users.data, selected],
  );
  const toggleDealer = (id: string): void =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // ---- options + aggregation ----------------------------------------------
  const [byDay, setByDay] = useState(false);
  const [includeIdle, setIncludeIdle] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('productViews');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [drawerUser, setDrawerUser] = useState<User | null>(null);

  const filteredLogs = useMemo(
    () => (selected.size ? logs.data.filter((l) => selected.has(l.userId)) : logs.data),
    [logs.data, selected],
  );
  const rows = useMemo(() => {
    const r = aggregateByDealer(selectedUsers, filteredLogs, { byDay, includeIdle });
    r.sort((a, b) => compareRows(a, b, sortKey, sortDir) || compareRows(a, b, 'events', 'desc') || a.user.name.localeCompare(b.user.name));
    return r;
  }, [selectedUsers, filteredLogs, byDay, includeIdle, sortKey, sortDir]);
  const totals = useMemo(() => reportTotals(filteredLogs), [filteredLogs]);
  const days = useMemo(() => (multiDay ? aggregateByDay(filteredLogs) : []), [filteredLogs, multiDay]);

  const toggleSort = (key: SortKey): void => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'dealer' || key === 'day' ? 'asc' : 'desc');
    }
  };

  // ---- exports -------------------------------------------------------------
  const slug = range ? rangeSlug(range) : 'range';
  const exportSummary = (): void => {
    const data = rows.map((r) => summaryCsvRow(r, (ts) => formatDateTime(ts)));
    downloadCsv(`activity-summary-${slug}${byDay ? '-by-day' : ''}.csv`, toCsv(data, SUMMARY_CSV_COLUMNS(byDay)));
  };
  const exportEvents = (): void => {
    const byId = new Map(users.data.map((u) => [u.id, u]));
    const data = filteredLogs
      .slice()
      .sort((a, b) => a.ts - b.ts)
      .map((l) => eventCsvRow(l, byId.get(l.userId), (ts) => formatDateTime(ts)));
    downloadCsv(`activity-events-${slug}.csv`, toCsv(data, EVENT_CSV_COLUMNS));
  };

  const dealerLabel = selected.size === 0 ? `All dealers (${users.data.length})` : `${selected.size} selected`;

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Activity report"
        subtitle="Who did what in the app, per dealer, for any day or range — and export it as CSV."
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={logs.reload} disabled={logs.loading || !range}>
              ↻ Refresh
            </Button>
            <Button variant="secondary" size="sm" onClick={exportEvents} disabled={filteredLogs.length === 0}>
              Export events CSV
            </Button>
            <Button size="sm" onClick={exportSummary} disabled={rows.length === 0}>
              Export summary CSV
            </Button>
          </>
        }
      />

      {/* ---------------------------- filters ---------------------------- */}
      <Card className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          {RANGE_PRESETS.map((p) => (
            <Button
              key={p.key}
              size="sm"
              variant={preset === p.key ? 'primary' : 'secondary'}
              onClick={() => setPreset(p.key)}
            >
              {p.label}
            </Button>
          ))}
          {preset === 'custom' && (
            <span className="flex items-center gap-2 text-sm text-slate-600">
              <input type="date" value={customFrom} max={inputDate(Date.now())} onChange={(e) => setCustomFrom(e.target.value)} className={inputCls} />
              <span>to</span>
              <input type="date" value={customTo} min={customFrom} max={inputDate(Date.now())} onChange={(e) => setCustomTo(e.target.value)} className={inputCls} />
            </span>
          )}
          <span className="ml-auto text-sm text-slate-600">
            {range ? describeRange(range) : 'Pick a valid date range'}
            {logs.fetchedAt && !logs.loading && (
              <span className="ml-2 text-xs text-slate-400">· loaded {timeAgo(logs.fetchedAt)}</span>
            )}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3">
          <Button variant={selected.size ? 'primary' : 'secondary'} size="sm" onClick={() => setPickerOpen((o) => !o)}>
            {dealerLabel} {pickerOpen ? '▴' : '▾'}
          </Button>
          {selected.size > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              Everyone ✕
            </Button>
          )}
          <label className="flex items-center gap-1.5 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={byDay}
              onChange={(e) => {
                // A day-wise table reads best newest day first; back to "most
                // product views" when it collapses to one row per dealer.
                setByDay(e.target.checked);
                setSortKey(e.target.checked ? 'day' : 'productViews');
                setSortDir('desc');
              }}
            />
            Group by day
          </label>
          <label className={`flex items-center gap-1.5 text-sm ${byDay ? 'text-slate-400' : 'text-slate-700'}`}>
            <input type="checkbox" checked={includeIdle} disabled={byDay} onChange={(e) => setIncludeIdle(e.target.checked)} />
            Include dealers with no activity
          </label>
          <span className="ml-auto text-xs text-slate-500">
            {logs.loading ? 'Loading events…' : `${filteredLogs.length.toLocaleString('en-IN')} events in range`}
          </span>
        </div>

        {pickerOpen && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="search"
                value={dealerSearch}
                onChange={(e) => setDealerSearch(e.target.value)}
                placeholder="Find a dealer — name, company, city, email, mobile…"
                className={`${inputCls} w-full sm:max-w-sm`}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelected((s) => new Set([...s, ...shownDealers.map((u) => u.id)]))}
                disabled={shownDealers.length === 0}
              >
                Select all shown ({shownDealers.length})
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} disabled={selected.size === 0}>
                Clear
              </Button>
            </div>
            <ul className="mt-2 grid max-h-64 gap-1 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3">
              {shownDealers.map((u) => (
                <li key={u.id}>
                  <label className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1 text-sm hover:bg-white">
                    <input type="checkbox" className="mt-0.5" checked={selected.has(u.id)} onChange={() => toggleDealer(u.id)} />
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-slate-800">{u.name}</span>
                      <span className="block truncate text-xs text-slate-500">
                        {u.companyName}
                        {u.city ? ` · ${u.city}` : ''}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
              {shownDealers.length === 0 && <li className="px-2 py-1 text-sm text-slate-500">No dealer matches.</li>}
            </ul>
          </div>
        )}
      </Card>

      {logs.truncated && (
        <Card className="border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          This range has more than {ACTIVITY_RANGE_MAX.toLocaleString('en-IN')} events; only the most recent ones are counted.
          Narrow the range for exact numbers.
        </Card>
      )}

      {/* ----------------------------- KPIs ------------------------------ */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard label="Active dealers" value={totals.activeDealers} sublabel={`of ${selectedUsers.length}`} tone="brand" loading={logs.loading} />
        <StatCard label="Sessions" value={totals.sessions} sublabel="app opens, hourly max" loading={logs.loading} />
        <StatCard label="Product views" value={totals.productViews} tone="emerald" loading={logs.loading} />
        <StatCard label="Distinct products" value={totals.distinctProducts} sublabel="across all dealers" loading={logs.loading} />
        <StatCard label="Searches" value={totals.searches} loading={logs.loading} />
        <StatCard label="Wishlist saves" value={totals.wishlisted} tone="amber" loading={logs.loading} />
      </div>

      {/* --------------------------- day-wise ---------------------------- */}
      {multiDay && days.length > 0 && (
        <Card className="overflow-hidden">
          <div className="border-b border-slate-100 px-4 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">
            Day by day
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 text-right font-medium">Active dealers</th>
                  <th className="px-4 py-2 text-right font-medium">Sessions</th>
                  <th className="px-4 py-2 text-right font-medium">Product views</th>
                  <th className="px-4 py-2 text-right font-medium">Searches</th>
                  <th className="px-4 py-2 text-right font-medium">Events</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {days.map((d) => (
                  <tr key={d.day}>
                    <td className="px-4 py-2 font-medium text-slate-800">{dayLabel(d.day)} <span className="text-xs text-slate-400">{d.day}</span></td>
                    <td className="px-4 py-2 text-right">{d.activeDealers}</td>
                    <td className="px-4 py-2 text-right">{d.sessions}</td>
                    <td className="px-4 py-2 text-right">{d.productViews}</td>
                    <td className="px-4 py-2 text-right">{d.searches}</td>
                    <td className="px-4 py-2 text-right">{d.events}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* --------------------------- per dealer -------------------------- */}
      {logs.error ? (
        <ErrorState message={logs.error} />
      ) : logs.loading || users.loading ? (
        <LoadingState label="Loading activity…" />
      ) : !range ? (
        <EmptyState title="Pick a date range" hint="Choose a preset above or enter custom dates." />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No activity in this range"
          hint={selected.size ? 'The selected dealers did nothing in the app on these dates.' : 'Nobody used the app on these dates.'}
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <Th label="Dealer" k="dealer" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  {byDay && <Th label="Date" k="day" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />}
                  <Th label="Sessions" k="sessions" right sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Product views" k="productViews" right sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Distinct products" k="distinctProducts" right sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Categories" k="distinctCategories" right sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Searches" k="searches" right sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Wishlist" k="wishlisted" right sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Events" k="events" right sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <th className="px-4 py-2 font-medium">Products viewed</th>
                  <Th label="Last activity" k="lastTs" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.key} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="min-w-0">
                          <div className="truncate font-medium text-slate-900">{r.user.name}</div>
                          <div className="truncate text-xs text-slate-500">
                            {r.user.companyName}
                            {r.user.city ? ` · ${r.user.city}` : ''}
                          </div>
                        </div>
                        {r.user.status !== 'approved' && <StatusPill status={r.user.status} />}
                      </div>
                    </td>
                    {byDay && (
                      <td className="whitespace-nowrap px-4 py-2 text-slate-700">{r.day ? dayLabel(r.day) : '—'}</td>
                    )}
                    <Num v={r.sessions} />
                    <Num v={r.productViews} strong />
                    <Num v={r.distinctProducts} />
                    <Num v={r.distinctCategories} />
                    <Num v={r.searches} />
                    <Num v={r.wishlisted} />
                    <Num v={r.events} />
                    <td className="max-w-[320px] px-4 py-2 text-xs text-slate-600" title={r.products.join('\n')}>
                      {r.products.length === 0 ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <>
                          <span className="line-clamp-2">{r.products.slice(0, 4).join(', ')}</span>
                          {r.products.length > 4 && <Badge tone="neutral">+{r.products.length - 4} more</Badge>}
                        </>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-slate-600" title={formatDateTime(r.lastTs)}>
                      {r.lastTs ? timeAgo(r.lastTs) : '—'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setDrawerUser(r.user)}>
                        Timeline
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
            {rows.length} row{rows.length === 1 ? '' : 's'} · {byDay ? 'one per dealer per day' : 'one per dealer'} ·{' '}
            {filteredLogs.length.toLocaleString('en-IN')} events
          </div>
        </Card>
      )}

      <ActivityTimeline user={drawerUser} open={drawerUser !== null} onClose={() => setDrawerUser(null)} />
    </div>
  );
}

function Th({
  label, k, right = false, sortKey, sortDir, onSort,
}: {
  label: string; k: SortKey; right?: boolean; sortKey: SortKey; sortDir: SortDir; onSort: (k: SortKey) => void;
}): JSX.Element {
  const active = sortKey === k;
  return (
    <th className={`px-4 py-2 font-medium ${right ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-slate-800 ${active ? 'text-slate-900' : ''}`}
      >
        {label}
        <span aria-hidden className={active ? 'text-brand-600' : 'text-slate-300'}>
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '▾'}
        </span>
      </button>
    </th>
  );
}

function Num({ v, strong = false }: { v: number; strong?: boolean }): JSX.Element {
  return (
    <td className={`px-4 py-2 text-right tabular-nums ${v === 0 ? 'text-slate-300' : strong ? 'font-semibold text-brand-700' : 'text-slate-800'}`}>
      {v}
    </td>
  );
}
